import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import searchIndex from '../data/searchIndex';

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const highlightMatch = useCallback((text, term) => {
    if (!term || term.length < 2) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} className="bg-yellow-200 text-gray-900 rounded px-0.5">{part}</mark>
        : part
    );
  }, []);

  const getExcerpt = useCallback((text, term) => {
    const lower = text.toLowerCase();
    const idx = lower.indexOf(term.toLowerCase());
    if (idx === -1) return text.slice(0, 180) + '...';
    const start = Math.max(0, idx - 80);
    const end = Math.min(text.length, idx + term.length + 100);
    let excerpt = '';
    if (start > 0) excerpt += '...';
    excerpt += text.slice(start, end);
    if (end < text.length) excerpt += '...';
    return excerpt;
  }, []);

  const handleSearch = useCallback((value) => {
    setQuery(value);
    if (value.length < 2) { setResults([]); return; }
    const term = value.toLowerCase();
    const found = searchIndex
      .filter(entry => entry.text.toLowerCase().includes(term))
      .map(entry => ({
        ...entry,
        excerpt: getExcerpt(entry.text, value),
      }));
    setResults(found);
  }, [getExcerpt]);

  const goToResult = useCallback((path) => {
    onClose();
    navigate(path);
    window.scrollTo(0, 0);
  }, [navigate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]" data-testid="search-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[75vh] flex flex-col" data-testid="search-container">
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <svg className="w-5 h-5 text-[#5BC5D4] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Rechercher sur le site... (ex: serviette, passeport, annulation)"
            className="flex-1 text-base text-gray-800 placeholder-gray-400 outline-none bg-transparent"
            data-testid="search-input"
          />
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition p-1"
            data-testid="search-close-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1 px-2 py-2">
          {query.length < 2 && (
            <div className="text-center py-12 text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-sm">Tapez au moins 2 caractères pour lancer la recherche</p>
            </div>
          )}

          {query.length >= 2 && results.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-base font-medium mb-1">Aucun résultat pour « {query} »</p>
              <p className="text-sm">Essayez un autre mot ou vérifiez l'orthographe</p>
            </div>
          )}

          {results.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 px-3 py-2">
                {results.length} résultat{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}
              </p>
              <div className="space-y-1">
                {results.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => goToResult(r.path)}
                    className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#F0FAFB] transition-colors group"
                    data-testid={`search-result-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-[#5BC5D4] bg-[#5BC5D4]/10 px-2 py-0.5 rounded-full">{r.page}</span>
                      <span className="text-xs text-gray-400">{r.section}</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {highlightMatch(r.excerpt, query)}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer shortcut */}
        <div className="border-t border-gray-100 px-5 py-2.5 flex items-center justify-between text-xs text-gray-400">
          <span>Recherche dans le contenu du site</span>
          <span className="hidden sm:inline">
            <kbd className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[10px] font-mono">Echap</kbd> pour fermer
          </span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
